/**
 * ClickInjectionDetectionMobilePage
 *
 * Mobile click-injection detection. Shows a submission form (event_id / device_id /
 * click_ts / install_ts + optional flags), a verdict summary (total / injected /
 * clean) with a top-reasons breakdown, and a recent-events table showing TTI
 * seconds and verdict per row.
 *
 * GREEN-only accent palette. No emerald / blue / orange / purple / dark / emoji.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useClickInjectionDetectionMobileList,
	useClickInjectionDetectionMobileCreate,
} from "./hooks";
import {
	reasonCounts,
	verdictCounts,
	type ClickInjectionDetectionMobileEvent,
	type InjectionVerdict,
} from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const H1 = "text-3xl font-semibold text-green-700";
const H2 = "text-base font-semibold text-zinc-900";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const LABEL = "text-xs font-medium text-zinc-700";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";

function fmtTime(unixSec: number): string {
	if (!unixSec) return "-";
	const d = new Date(unixSec * 1000);
	return d.toISOString().replace("T","").slice(0, 19);
}

function verdictBadge(v: InjectionVerdict): string {
	if (v === "injected") {
 return "rounded-[6px] border border-green-300 bg-green-50 px-2 py-0.5 font-mono text-xs text-green-900";
	}
	return "rounded-[6px] border border-zinc-300 bg-zinc-50 px-2 py-0.5 font-mono text-xs text-zinc-700";
}

export function ClickInjectionDetectionMobilePage() {
	const listQuery = useClickInjectionDetectionMobileList();
	const createMut = useClickInjectionDetectionMobileCreate();

	const [eventId, setEventId] = useState("");
	const [deviceId, setDeviceId] = useState("");
	const [clickTs, setClickTs] = useState("");
	const [installTs, setInstallTs] = useState("");
	const [alreadyInstalled, setAlreadyInstalled] = useState(false);
	const [referrerTs, setReferrerTs] = useState("");

	const items: ClickInjectionDetectionMobileEvent[] = useMemo(
 () => listQuery.data?.items ?? [],
 [listQuery.data],
	);
	const counts = useMemo(() => verdictCounts(items), [items]);
	const reasons = useMemo(() => reasonCounts(items), [items]);

	const submitDisabled =
 createMut.isPending ||
 !eventId.trim() ||
 !deviceId.trim() ||
 !clickTs.trim() ||
 !installTs.trim();

	const handleCreate = (e: FormEvent) => {
 e.preventDefault();
 if (submitDisabled) return;
 const click = Number(clickTs);
 const install = Number(installTs);
 if (!Number.isFinite(click) || !Number.isFinite(install)) return;
 const refTrimmed = referrerTs.trim();
 const refNum = refTrimmed === "" ? null : Number(refTrimmed);
 createMut.mutate(
 {
 event_id: eventId.trim(),
 device_id: deviceId.trim(),
 click_ts: click,
 install_ts: install,
 was_already_installed: alreadyInstalled,
 install_referrer_ts:
 refNum === null || !Number.isFinite(refNum) ? null : refNum,
 },
 {
 onSuccess: () => {
 setEventId("");
 setDeviceId("");
 setClickTs("");
 setInstallTs("");
 setAlreadyInstalled(false);
 setReferrerTs("");
 },
 },
 );
	};

	return (
 <main
 data-testid="click-injection-detection-mobile-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
 click-injection-detection-mobile
 </p>
 <h1 className={H1}>Click injection detection (mobile)</h1>
 <p className="text-sm text-zinc-600">
 Classify mobile install events by time-to-install, install referrer
 and prior-install signals.
 </p>
 </header>

 <section className={CARD} aria-label="Verdict summary">
 <h2 className={H2}>Verdict summary</h2>
 <dl
 className="mt-3 grid grid-cols-3 gap-3 text-center"
 data-testid="click-injection-detection-mobile-summary"
 >
 <div className="rounded-[6px] border border-zinc-200 bg-zinc-50 px-3 py-2">
 <dt className="text-xs uppercase tracking-wider text-zinc-500">
 Total
 </dt>
 <dd
 className="mt-1 text-2xl font-semibold text-zinc-900"
 data-testid="click-injection-detection-mobile-total"
 >
 {counts.total}
 </dd>
 </div>
 <div className="rounded-[6px] border border-green-300 bg-green-50 px-3 py-2">
 <dt className="text-xs uppercase tracking-wider text-green-800">
 Injected
 </dt>
 <dd
 className="mt-1 text-2xl font-semibold text-green-900"
 data-testid="click-injection-detection-mobile-injected"
 >
 {counts.injected}
 </dd>
 </div>
 <div className="rounded-[6px] border border-zinc-200 bg-white px-3 py-2">
 <dt className="text-xs uppercase tracking-wider text-zinc-500">
 Clean
 </dt>
 <dd
 className="mt-1 text-2xl font-semibold text-zinc-900"
 data-testid="click-injection-detection-mobile-clean"
 >
 {counts.clean}
 </dd>
 </div>
 </dl>

 <h3 className="text-green-700 font-semibold mt-5 text-sm font-semibold text-zinc-800">
 Top injection reasons
 </h3>
 {reasons.length === 0 ? (
 <p
 className="mt-2 text-xs text-zinc-500"
 data-testid="click-injection-detection-mobile-reasons-empty"
 >
 No injections detected yet.
 </p>
 ) : (
 <ul
 className="mt-2 flex flex-wrap gap-2"
 data-testid="click-injection-detection-mobile-reasons"
 >
 {reasons.map((r) => (
 <li
 key={r.reason}
 className="rounded-[6px] border border-green-300 bg-green-50 px-2 py-1 text-xs text-green-900"
 data-testid={`click-injection-detection-mobile-reason-${r.reason}`}
 >
 <span className="font-medium">{r.reason}</span>
 <span className="ml-1 font-mono text-green-700">
 {r.count}
 </span>
 </li>
 ))}
 </ul>
 )}
 </section>

 <section className={CARD} aria-label="Submit event for classification">
 <h2 className={H2}>Submit event</h2>
 <p className="mt-1 text-xs text-zinc-500">
 Timestamps are unix-seconds. The server classifies TTI &lt; 10s,
 referrer-before-click and pre-existing-install as injection.
 </p>
 <form
 onSubmit={handleCreate}
 className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2"
 data-testid="click-injection-detection-mobile-create-form"
 >
 <label className="flex flex-col gap-1">
 <span className={LABEL}>Event ID</span>
 <input
 type="text"
 className={INPUT}
 placeholder="evt_123"
 value={eventId}
 onChange={(e) => setEventId(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="click-injection-detection-mobile-event-id"
 />
 </label>
 <label className="flex flex-col gap-1">
 <span className={LABEL}>Device ID</span>
 <input
 type="text"
 className={INPUT}
 placeholder="gaid_or_idfa"
 value={deviceId}
 onChange={(e) => setDeviceId(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="click-injection-detection-mobile-device-id"
 />
 </label>
 <label className="flex flex-col gap-1">
 <span className={LABEL}>Click timestamp (unix s)</span>
 <input
 type="number"
 className={INPUT}
 placeholder="1713600000"
 value={clickTs}
 onChange={(e) => setClickTs(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="click-injection-detection-mobile-click-ts"
 />
 </label>
 <label className="flex flex-col gap-1">
 <span className={LABEL}>Install timestamp (unix s)</span>
 <input
 type="number"
 className={INPUT}
 placeholder="1713600050"
 value={installTs}
 onChange={(e) => setInstallTs(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="click-injection-detection-mobile-install-ts"
 />
 </label>
 <label className="flex flex-col gap-1">
 <span className={LABEL}>Install referrer timestamp (optional)</span>
 <input
 type="number"
 className={INPUT}
 placeholder="blank if unknown"
 value={referrerTs}
 onChange={(e) => setReferrerTs(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="click-injection-detection-mobile-referrer-ts"
 />
 </label>
 <label className="flex items-center gap-2 pt-6">
 <input
 type="checkbox"
 checked={alreadyInstalled}
 onChange={(e) => setAlreadyInstalled(e.currentTarget.checked)}
 disabled={createMut.isPending}
 className="h-4 w-4 accent-green-700"
 data-testid="click-injection-detection-mobile-already-installed"
 />
 <span className="text-xs text-zinc-700">
 App already installed on device
 </span>
 </label>
 <div className="sm:col-span-2">
 <button
 type="submit"
 className={BTN}
 disabled={submitDisabled}
 data-testid="click-injection-detection-mobile-submit"
 >
 {createMut.isPending ? "Classifying." : "Classify event"}
 </button>
 {createMut.error ? (
 <p
 className="mt-2 text-xs text-red-600"
 role="alert"
 data-testid="click-injection-detection-mobile-create-error"
 >
 {createMut.error.message}
 </p>
 ) : null}
 {createMut.data ? (
 <p
 className="mt-2 text-xs text-zinc-700"
 data-testid="click-injection-detection-mobile-create-result"
 >
 Last:{""}
 <span className="font-mono text-green-800">
 {createMut.data.verdict}
 </span>
 {createMut.data.reason ? (
 <>
 {""}
 <span className="text-zinc-500">
 ({createMut.data.reason})
 </span>
 </>
 ) : null}
 <span className="ml-1 text-zinc-500">
 TTI {createMut.data.tti_seconds}s
 </span>
 </p>
 ) : null}
 </div>
 </form>
 </section>

 <section className={CARD} aria-label="Recent events">
 <h2 className={H2}>Recent events</h2>
 {listQuery.isLoading ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="click-injection-detection-mobile-loading"
 >
 Loading.
 </p>
 ) : listQuery.error ? (
 <p
 className="mt-3 text-sm text-red-600"
 role="alert"
 data-testid="click-injection-detection-mobile-list-error"
 >
 {listQuery.error.message}
 </p>
 ) : items.length === 0 ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="click-injection-detection-mobile-empty"
 >
 No events yet.
 </p>
 ) : (
 <div className="mt-3 overflow-x-auto">
 <table
 className="w-full text-left text-sm"
 data-testid="click-injection-detection-mobile-table"
 >
 <thead>
 <tr className="border-b border-zinc-200 text-xs uppercase text-zinc-500">
 <th className="py-2 pr-4">When</th>
 <th className="py-2 pr-4">Event</th>
 <th className="py-2 pr-4">Device</th>
 <th className="py-2 pr-4">TTI s</th>
 <th className="py-2 pr-4">Verdict</th>
 <th className="py-2">Reason</th>
 </tr>
 </thead>
 <tbody>
 {items.map((row) => (
 <tr
 key={row.id}
 className="border-b border-zinc-100"
 data-testid={`click-injection-detection-mobile-row-${row.id}`}
 >
 <td className="py-2 pr-4 font-mono text-xs text-zinc-600">
 {fmtTime(row.created_at)}
 </td>
 <td className="py-2 pr-4 font-mono text-xs text-zinc-800">
 {row.event_id}
 </td>
 <td className="py-2 pr-4 font-mono text-xs text-zinc-700">
 <span
 className="block max-w-[10rem] truncate"
 title={row.device_id}
 >
 {row.device_id}
 </span>
 </td>
 <td className="py-2 pr-4 font-mono text-xs text-zinc-800">
 {row.tti_seconds}
 </td>
 <td className="py-2 pr-4">
 <span
 className={verdictBadge(row.verdict)}
 data-testid={`click-injection-detection-mobile-verdict-${row.id}`}
 >
 {row.verdict}
 </span>
 </td>
 <td className="py-2 text-xs text-zinc-700">
 {row.reason ?? "-"}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </section>
 </main>
	);
}

export default ClickInjectionDetectionMobilePage;
